import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import type {
  DispatchPreferredRateMethod,
  DispatchRateLocation,
} from '../dispatch-freight/models/dispatch-freight.models';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService,
  ContactDetails,
} from '../services/contacts/contact-autocomplete.service';
import {
  FreightCommandLaneRole,
  FreightCommandWorkspaceService,
  FreightCommandWorkspaceState,
} from './shared/freight-command-workspace.service';

interface FreightCommandNavigationItem {
  label: string;
  description: string;
  route: 'spot-quote' | 'freightmancer-ascendant';
}

interface FreightCommandAddressRecord {
  contact_address_uuid?: unknown;
  address?: unknown;
  address_extended?: unknown;
  locality?: unknown;
  region?: unknown;
  postal_code?: unknown;
  country?: unknown;
  country_code?: unknown;
}

interface FreightCommandAddressOption {
  value: string;
  label: string;
  location: DispatchRateLocation;
}

@Component({
  selector: 'app-freight-command-shell',
  templateUrl: './freight-command-shell.component.html',
  styleUrls: ['./freight-command-shell.component.scss'],
})
export class FreightCommandShellComponent implements OnInit, OnDestroy {
  readonly navigation: FreightCommandNavigationItem[] = [
    {
      label: 'Spot Quote',
      description: 'Build, rate, condition, and review an internal quote.',
      route: 'spot-quote',
    },
    {
      label: 'The Freightmancer',
      description: 'Build, rate, and append freight to an existing lading.',
      route: 'freightmancer-ascendant',
    },
  ];

  readonly customerControl = new FormControl<ContactAutocompleteResult | string | null>(null);
  readonly originControl = new FormControl<ContactAutocompleteResult | string | null>(null);
  readonly destinationControl = new FormControl<ContactAutocompleteResult | string | null>(null);

  readonly addressOptions: Record<FreightCommandLaneRole, FreightCommandAddressOption[]> = {
    origin: [],
    destination: [],
  };

  readonly addressLoading: Record<FreightCommandLaneRole, boolean> = {
    origin: false,
    destination: false,
  };

  readonly addressError: Record<FreightCommandLaneRole, string> = {
    origin: '',
    destination: '',
  };

  readonly partyName: Record<FreightCommandLaneRole, string> = {
    origin: '',
    destination: '',
  };

  state: FreightCommandWorkspaceState;

  private readonly detailSequence: Record<FreightCommandLaneRole, number> = {
    origin: 0,
    destination: 0,
  };
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly contactService: ContactAutocompleteService,
    private readonly workspace: FreightCommandWorkspaceService,
  ) {
    this.state = this.workspace.snapshot;
  }

  get shipmentReady(): boolean {
    return Boolean(
      this.state.customer_contact_uuid
      && this.laneReady('origin')
      && this.laneReady('destination'),
    );
  }

  get hasWorkspaceState(): boolean {
    return Boolean(
      this.state.customer_contact_uuid
      || this.state.context.origin.contact_uuid
      || this.state.context.destination.contact_uuid
      || this.state.draft.items.length
      || this.state.rating_request
      || this.state.quote_create_result,
    );
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.workspace.state$.subscribe((state) => {
        this.state = state;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onCustomerSelected(selected: ContactAutocompleteResult): void {
    this.workspace.setCustomer(selected.value, selected.label);
  }

  onLaneContactSelected(
    role: FreightCommandLaneRole,
    selected: ContactAutocompleteResult,
  ): void {
    const sequence = ++this.detailSequence[role];
    this.partyName[role] = selected.label;
    this.addressOptions[role] = [];
    this.addressError[role] = '';
    this.addressLoading[role] = true;
    this.workspace.setLocation(role, { contact_uuid: selected.value });

    this.subscriptions.add(
      this.contactService.getDetails(selected.value).subscribe({
        next: (details) => {
          if (sequence !== this.detailSequence[role]) return;
          const options = this.addressesFromDetails(selected.value, details);
          this.addressOptions[role] = options;
          this.addressLoading[role] = false;

          if (options.length === 0) {
            this.addressError[role] = 'This contact has no selectable address.';
            return;
          }

          this.workspace.setLocation(role, options[0].location);
        },
        error: () => {
          if (sequence !== this.detailSequence[role]) return;
          this.addressLoading[role] = false;
          this.addressError[role] = 'Address lookup failed. Select the contact again to retry.';
        },
      }),
    );
  }

  onAddressSelected(role: FreightCommandLaneRole, event: Event): void {
    const value = event.target instanceof HTMLSelectElement ? event.target.value : '';
    const option = this.addressOptions[role].find((address) => address.value === value);
    if (option) this.workspace.setLocation(role, option.location);
  }

  onRateMethodSelected(event: Event): void {
    const value = event.target instanceof HTMLSelectElement ? Number(event.target.value) : 0;
    const method: DispatchPreferredRateMethod | null =
      value === 1 || value === 2 || value === 3 || value === 4 ? value : null;
    this.workspace.setPreferredRateMethod(method);
  }

  resetWorkspace(): void {
    this.detailSequence.origin += 1;
    this.detailSequence.destination += 1;
    this.workspace.reset();
    this.customerControl.setValue(null);
    this.originControl.setValue(null);
    this.destinationControl.setValue(null);
    this.addressOptions.origin = [];
    this.addressOptions.destination = [];
    this.addressLoading.origin = false;
    this.addressLoading.destination = false;
    this.addressError.origin = '';
    this.addressError.destination = '';
    this.partyName.origin = '';
    this.partyName.destination = '';
  }

  laneReady(role: FreightCommandLaneRole): boolean {
    const location = this.state.context[role];
    return Boolean(
      location.contact_uuid
      && location.contact_address_uuid
      && (location.postal_code || (location.city && location.province)),
    );
  }

  locationSummary(role: FreightCommandLaneRole): string {
    const location = this.state.context[role];
    const locality = [location.city, location.province]
      .filter((value): value is string => Boolean(value))
      .join(', ');
    return [locality, location.postal_code?.toUpperCase()]
      .filter((value): value is string => Boolean(value))
      .join(' · ') || 'Address required';
  }

  trackAddress(_index: number, address: FreightCommandAddressOption): string {
    return address.value;
  }

  private addressesFromDetails(
    contactUuid: string,
    details: ContactDetails,
  ): FreightCommandAddressOption[] {
    return ((details.addresses || []) as FreightCommandAddressRecord[])
      .map((address) => this.addressOption(contactUuid, address))
      .filter((option): option is FreightCommandAddressOption => option !== null);
  }

  private addressOption(
    contactUuid: string,
    address: FreightCommandAddressRecord,
  ): FreightCommandAddressOption | null {
    const contactAddressUuid = this.cleanString(address.contact_address_uuid);
    if (!contactAddressUuid) return null;

    const street = this.cleanString(address.address);
    const extended = this.cleanString(address.address_extended);
    const city = this.cleanString(address.locality);
    const province = this.cleanString(address.region);
    const postalCode = this.cleanString(address.postal_code);
    const country = this.cleanString(address.country_code)
      || this.cleanString(address.country);
    const place = [city, province].filter(Boolean).join(', ');
    const label = [street, extended, place, postalCode, country]
      .filter(Boolean)
      .join(' · ');

    return {
      value: contactAddressUuid,
      label: label || contactAddressUuid,
      location: {
        contact_uuid: contactUuid,
        contact_address_uuid: contactAddressUuid,
        postal_code: postalCode || undefined,
        city: city || undefined,
        province: province || undefined,
        country: country || undefined,
      },
    };
  }

  private cleanString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
